/**
 * Side Navigation Configuration — Story 13.2
 *
 * Static route-to-context mapping and nav section definitions.
 * RBAC permission requirements are declared per item.
 */

import type { LucideIcon } from "lucide-react";
import {
  Bot,
  FolderOpen,
  Play,
  FileText,
  Calendar,
  History,
  ShieldCheck,
  ClipboardList,
  BarChart3,
  Zap,
  Settings,
  Cpu,
  Cog,
  Scale,
  BookOpen,
  Database,
  Tag,
  Bell,
  ScrollText,
  Download,
  Search,
  LayoutDashboard,
  Clock,
  ArrowLeftRight,
  HeartPulse,
  Gauge,
  Server,
  GitCompare,
  RotateCcw,
} from "lucide-react";

import type { SideNavContext } from "../../context/persona-context";
import { Microscope, FileUp } from "lucide-react";

// ============================================================
// Types
// ============================================================

export interface NavPermission {
  /** Permission string matching backend RBAC (e.g., 'pipeline:execute', 'admin:manage') */
  requiredPermission: string;
  /** If true, item is visible in read-only mode when user has the 'read' variant */
  allowReadOnly?: boolean;
}

export interface NavItemConfig {
  id: string;
  label: string;
  path: string;
  icon: LucideIcon;
  permission?: NavPermission;
  badge?: {
    /** SSE event type that drives this badge count */
    sseEventType: string;
    /** Zustand store selector key for the count value */
    storeKey: string;
  };
  children?: NavItemConfig[];
}

export interface NavSectionConfig {
  id: string;
  label: string;
  context: SideNavContext;
  items: NavItemConfig[];
}

// ============================================================
// Route-to-Context Mapping (longest prefix first)
// ============================================================

interface RouteContextMapping {
  prefix: string;
  context: SideNavContext;
}

const ROUTE_CONTEXT_MAP: RouteContextMapping[] = [
  { prefix: "/agents/", context: "pipeline" },
  { prefix: "/pipelines/", context: "pipeline" },
  { prefix: "/pipelines", context: "pipeline" },
  { prefix: "/reviews/", context: "review" },
  { prefix: "/reviews", context: "review" },
  { prefix: "/admin/", context: "admin" },
  { prefix: "/admin", context: "admin" },
  { prefix: "/governance/", context: "governance" },
  { prefix: "/governance", context: "governance" },
  { prefix: "/ops/", context: "ops" },
  { prefix: "/ops", context: "ops" },
  { prefix: "/audit/", context: "audit" },
  { prefix: "/audit", context: "audit" },
  { prefix: "/reports/", context: "governance" },
  { prefix: "/reports", context: "governance" },
  { prefix: "/deploy/", context: "admin" },
  { prefix: "/deploy", context: "admin" },
  { prefix: "/profiler/", context: "profiler" },
  { prefix: "/profiler", context: "profiler" },
  { prefix: "/feed", context: "home" },
];

/**
 * Derive the nav context from the current pathname.
 * Matches longest prefix first for specificity.
 */
export function deriveNavContext(pathname: string): SideNavContext {
  for (const { prefix, context } of ROUTE_CONTEXT_MAP) {
    if (pathname === prefix || pathname.startsWith(prefix)) {
      return context;
    }
  }
  return "home";
}

// ============================================================
// Nav Section Definitions
// ============================================================

const HOME_SECTION: NavSectionConfig = {
  id: "home",
  label: "Home",
  context: "home",
  items: [
    // Agents (Data Profiler, DQ Rules Engine, etc.) intentionally do NOT
    // appear here. All agent execution is scoped to a Project — users
    // must open a project first, then launch an agent from within that
    // project's workspace. This enforces tenant/project boundaries for
    // permissions, audit, and data classification (GPS Tier 2 rules).
    { id: "projects", label: "Projects", path: "/projects", icon: FolderOpen },
    {
      id: "outputs",
      label: "Outputs",
      path: "/outputs",
      icon: Download,
      permission: { requiredPermission: "profiler:read", allowReadOnly: true },
    },
  ],
};

const PIPELINE_SECTION: NavSectionConfig = {
  id: "pipeline",
  label: "Pipelines",
  context: "pipeline",
  items: [
    {
      id: "active-runs",
      label: "Active Runs",
      path: "/pipelines/active",
      icon: Play,
      permission: { requiredPermission: "pipeline:read", allowReadOnly: true },
      badge: { sseEventType: "pipeline:status", storeKey: "activeRunCount" },
    },
    {
      id: "templates",
      label: "Templates",
      path: "/pipelines/templates",
      icon: FileText,
      permission: { requiredPermission: "pipeline:read", allowReadOnly: true },
    },
    {
      id: "schedules",
      label: "Schedules",
      path: "/pipelines/schedules",
      icon: Calendar,
      permission: { requiredPermission: "pipeline:execute" },
    },
    {
      id: "run-history",
      label: "Run History",
      path: "/pipelines/history",
      icon: History,
      permission: { requiredPermission: "pipeline:read", allowReadOnly: true },
    },
  ],
};

const ADMIN_SECTION: NavSectionConfig = {
  id: "admin",
  label: "Administration",
  context: "admin",
  items: [
    {
      id: "agent-catalog",
      label: "Agent Catalog",
      path: "/admin/agents",
      icon: Bot,
      permission: { requiredPermission: "admin:manage" },
    },
    {
      id: "tenant-config",
      label: "Tenant Config",
      path: "/admin/tenant",
      icon: Settings,
      permission: { requiredPermission: "admin:manage" },
    },
    {
      id: "worker-scaling",
      label: "Worker Scaling",
      path: "/admin/workers",
      icon: Cpu,
      permission: { requiredPermission: "admin:manage" },
    },
    {
      id: "system-settings",
      label: "System Settings",
      path: "/admin/settings",
      icon: Cog,
      permission: { requiredPermission: "admin:manage" },
    },
  ],
};

const REVIEW_SECTION: NavSectionConfig = {
  id: "review",
  label: "Reviews",
  context: "review",
  items: [
    {
      id: "pending-items",
      label: "Pending Items",
      path: "/reviews/pending",
      icon: ClipboardList,
      permission: { requiredPermission: "review:read", allowReadOnly: true },
      badge: { sseEventType: "review:pending", storeKey: "pendingReviewCount" },
    },
    {
      id: "my-reviews",
      label: "My Reviews",
      path: "/reviews/mine",
      icon: ShieldCheck,
      permission: { requiredPermission: "review:read", allowReadOnly: true },
    },
    {
      id: "queue-stats",
      label: "Queue Stats",
      path: "/reviews/stats",
      icon: BarChart3,
      permission: { requiredPermission: "review:read", allowReadOnly: true },
    },
    {
      id: "auto-advance-settings",
      label: "Auto-Advance Settings",
      path: "/reviews/settings",
      icon: Zap,
      permission: { requiredPermission: "review:write" },
    },
  ],
};

const GOVERNANCE_SECTION: NavSectionConfig = {
  id: "governance",
  label: "Governance",
  context: "governance",
  items: [
    {
      id: "compliance-dashboard",
      label: "Compliance Dashboard",
      path: "/governance/compliance",
      icon: Scale,
      permission: {
        requiredPermission: "governance:read",
        allowReadOnly: true,
      },
    },
    {
      id: "audit-trail",
      label: "Audit Trail",
      path: "/governance/audit",
      icon: BookOpen,
      permission: {
        requiredPermission: "governance:read",
        allowReadOnly: true,
      },
    },
    {
      id: "data-dictionary",
      label: "Data Dictionary",
      path: "/governance/dictionary",
      icon: Database,
      permission: {
        requiredPermission: "governance:read",
        allowReadOnly: true,
      },
    },
    {
      id: "classification-rules",
      label: "Classification Rules",
      path: "/governance/classification",
      icon: Tag,
      permission: { requiredPermission: "governance:write" },
    },
  ],
};

const OPS_SECTION: NavSectionConfig = {
  id: "ops",
  label: "Operations",
  context: "ops",
  items: [
    {
      id: "health-dashboard",
      label: "Health Dashboard",
      path: "/ops/health",
      icon: HeartPulse,
      permission: { requiredPermission: "ops:read", allowReadOnly: true },
    },
    {
      id: "alerts",
      label: "Alerts",
      path: "/ops/alerts",
      icon: Bell,
      permission: { requiredPermission: "ops:read", allowReadOnly: true },
      badge: { sseEventType: "ops:alert", storeKey: "activeAlertCount" },
    },
    {
      id: "logs",
      label: "Logs",
      path: "/ops/logs",
      icon: ScrollText,
      permission: { requiredPermission: "ops:read", allowReadOnly: true },
    },
    {
      id: "queue-metrics",
      label: "Queue Metrics",
      path: "/ops/queues",
      icon: Gauge,
      permission: { requiredPermission: "ops:read", allowReadOnly: true },
    },
    {
      id: "worker-status",
      label: "Worker Status",
      path: "/ops/workers",
      icon: Server,
      permission: { requiredPermission: "ops:read", allowReadOnly: true },
    },
  ],
};

const AUDIT_SECTION: NavSectionConfig = {
  id: "audit",
  label: "Audit",
  context: "audit",
  items: [
    {
      id: "trail-search",
      label: "Trail Search",
      path: "/audit/search",
      icon: Search,
      permission: { requiredPermission: "audit:read" },
    },
    {
      id: "export-reports",
      label: "Export Reports",
      path: "/audit/export",
      icon: Download,
      permission: { requiredPermission: "audit:read" },
    },
    {
      id: "compliance-attestations",
      label: "Compliance Attestations",
      path: "/audit/attestations",
      icon: ShieldCheck,
      permission: { requiredPermission: "audit:read" },
    },
    {
      id: "correlation-explorer",
      label: "Correlation Explorer",
      path: "/audit/correlations",
      icon: ArrowLeftRight,
      permission: { requiredPermission: "audit:read" },
    },
  ],
};

const DEPLOY_SECTION: NavSectionConfig = {
  id: "deploy",
  label: "Deployment",
  context: "admin",
  items: [
    {
      id: "environments",
      label: "Environments",
      path: "/deploy/environments",
      icon: Server,
      permission: { requiredPermission: "admin:manage" },
    },
    {
      id: "release-history",
      label: "Release History",
      path: "/deploy/releases",
      icon: GitCompare,
      permission: { requiredPermission: "admin:manage" },
    },
    {
      id: "rollback",
      label: "Rollback",
      path: "/deploy/rollback",
      icon: RotateCcw,
      permission: { requiredPermission: "admin:manage" },
    },
  ],
};

const PROFILER_SECTION: NavSectionConfig = {
  id: "profiler",
  label: "Data Profiler",
  context: "profiler",
  items: [
    {
      id: "profiler-dashboard",
      label: "Profiler Dashboard",
      path: "/profiler",
      icon: Microscope,
      permission: { requiredPermission: "profiler:read", allowReadOnly: true },
    },
    {
      id: "new-profile",
      label: "New Profile",
      path: "/profiler/new",
      icon: FileUp,
      permission: { requiredPermission: "profiler:execute" },
    },
    {
      id: "profiler-history",
      label: "Run History",
      path: "/profiler/history",
      icon: History,
      permission: { requiredPermission: "profiler:read", allowReadOnly: true },
    },
    {
      id: "profiler-settings",
      label: "Settings",
      path: "/profiler/settings",
      icon: Settings,
      permission: { requiredPermission: "profiler:configure" },
    },
  ],
};

const REPORTS_SECTION: NavSectionConfig = {
  id: "reports",
  label: "Reports",
  context: "governance",
  items: [
    {
      id: "dashboards",
      label: "Dashboards",
      path: "/reports/dashboards",
      icon: LayoutDashboard,
      permission: { requiredPermission: "reports:read", allowReadOnly: true },
    },
    {
      id: "scheduled-reports",
      label: "Scheduled Reports",
      path: "/reports/scheduled",
      icon: Clock,
      permission: { requiredPermission: "reports:read", allowReadOnly: true },
    },
    {
      id: "export-history",
      label: "Export History",
      path: "/reports/exports",
      icon: Download,
      permission: { requiredPermission: "reports:read", allowReadOnly: true },
    },
  ],
};

// ============================================================
// All sections indexed by context
// ============================================================

export const NAV_SECTIONS_BY_CONTEXT: Record<
  SideNavContext,
  NavSectionConfig[]
> = {
  home: [HOME_SECTION],
  pipeline: [PIPELINE_SECTION],
  admin: [ADMIN_SECTION, DEPLOY_SECTION],
  review: [REVIEW_SECTION],
  governance: [GOVERNANCE_SECTION, REPORTS_SECTION],
  ops: [OPS_SECTION],
  audit: [AUDIT_SECTION],
  profiler: [PROFILER_SECTION],
  agent: [PIPELINE_SECTION], // Agent workspace inherits pipeline context
};

// ============================================================
// Permission Filtering
// ============================================================

/**
 * Check whether a user has the required permission for a nav item.
 */
export function hasNavPermission(
  userPermissions: string[],
  permission?: NavPermission,
): boolean {
  if (!permission) return true; // No permission required

  // Platform-wide wildcard -- this is the literal permission codename the
  // backend RBAC seed grants the platform_admin role (see seed.py), not
  // "*:*". Without this, an admin session appears to have zero nav access
  // the moment real (non-empty) permissions are wired in from the backend.
  if (userPermissions.includes("*")) return true;

  const { requiredPermission, allowReadOnly } = permission;

  // Direct match
  if (userPermissions.includes(requiredPermission)) return true;

  // Read-only fallback: if item allows read-only and user has :read variant
  if (allowReadOnly) {
    const resource = requiredPermission.split(":")[0];
    if (
      userPermissions.includes(`${resource}:read`) ||
      userPermissions.includes(`${resource}:*`)
    ) {
      return true;
    }
  }

  // Wildcard check
  const resource = requiredPermission.split(":")[0];
  if (
    userPermissions.includes(`${resource}:*`) ||
    userPermissions.includes("*:*")
  ) {
    return true;
  }

  return false;
}

/**
 * Filter nav items based on user permissions.
 * Returns only items the user has access to view.
 */
export function filterNavItems(
  items: NavItemConfig[],
  userPermissions: string[],
): NavItemConfig[] {
  return items.filter((item) =>
    hasNavPermission(userPermissions, item.permission),
  );
}

/**
 * Check if a path matches the current location (for active state).
 */
export function isActiveItem(itemPath: string, currentPath: string): boolean {
  if (currentPath === itemPath) return true;
  if (currentPath.startsWith(itemPath + "/")) return true;
  return false;
}
